import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaArrowLeft, FaSearch, FaFilter, FaTrash } from 'react-icons/fa';
import { getInvitationsByEmail, deleteCampaignInvitationByEmail } from '../../services/companyAuthService';
import ConfirmModal from '../../../components/ConfirmModal';

const AffiliateCampaigns = () => {
    const { email } = useParams();
    const navigate = useNavigate();
    const [affiliate, setAffiliate] = useState(null);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [deletingCampaign, setDeletingCampaign] = useState(null);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [campaignPendingDelete, setCampaignPendingDelete] = useState(null);

    useEffect(() => {
        fetchAffiliateDetails();
    }, [email]);

    const fetchAffiliateDetails = async () => {
        try {
            setLoading(true);
            const data = await getInvitationsByEmail(email);
            
            if (data.campaigns) {
                // Normalize campaign data structure
                data.campaigns = data.campaigns.map(campaignItem => ({
                    campaign: campaignItem.campaign || campaignItem,
                    status: campaignItem.status,
                    _id: campaignItem._id,
                    inviteId: campaignItem.inviteId // Preserve the inviteId
                }));
            }
            
            setAffiliate(data);
        } catch (error) {
            console.error('Error fetching affiliate details:', error);
            toast.error(error.message || 'Failed to fetch affiliate details');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteCampaign = async (campaignItem) => {
        if (campaignItem.status === 'accepted') {
            toast.error('Cannot delete accepted campaign invitations');
            return;
        }

        try {
            setDeletingCampaign(campaignItem._id);
            
            // Use the email-based deletion service
            const response = await deleteCampaignInvitationByEmail(email, campaignItem.campaign._id);

            if (response.success) {
                toast.success('Campaign invitation deleted successfully');
                // Refresh the data
                await fetchAffiliateDetails();
            } else {
                toast.error(response.message || 'Failed to delete campaign invitation');
            }
        } catch (error) {
            console.error('Error deleting campaign invitation:', error);
            toast.error(error.message || 'Failed to delete campaign invitation');
        } finally {
            setDeletingCampaign(null);
        }
    };

    const requestDeleteCampaign = (campaignItem) => {
        if (campaignItem.status === 'accepted') {
            toast.error('Cannot delete accepted campaign invitations');
            return;
        }
        setCampaignPendingDelete(campaignItem);
        setConfirmOpen(true);
    };

    const getStatusBadge = (status) => {
        const statusConfig = {
            pending: { bg: 'bg-yellow-100', text: 'text-yellow-800', icon: '⏳' },
            accepted: { bg: 'bg-green-100', text: 'text-green-800', icon: '✓' },
            rejected: { bg: 'bg-red-100', text: 'text-red-800', icon: '✕' }
        };

        const config = statusConfig[status] || { bg: 'bg-gray-100', text: 'text-gray-800', icon: '•' };

        return (
            <span className={`px-3 py-1 inline-flex items-center gap-1 text-xs leading-5 font-semibold rounded-full ${config.bg} ${config.text}`}>
                <span>{config.icon}</span>
                {status.charAt(0).toUpperCase() + status.slice(1)}
            </span>
        );
    };

    const filteredCampaigns = affiliate?.campaigns?.filter(campaignItem => {
        const campaignData = campaignItem.campaign;
        const matchesSearch =
            campaignData.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            campaignData.description?.toLowerCase().includes(searchTerm.toLowerCase());

        const matchesStatus = statusFilter === 'all' || campaignItem.status === statusFilter;

        return matchesSearch && matchesStatus;
    }) || [];

    if (loading) {
        return (
            <div className="flex justify-center items-center min-h-full bg-gray-50">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
            </div>
        );
    }

    if (!affiliate) {
        return (
            <div className="flex justify-center items-center min-h-full bg-gray-50">
                <div className="text-center">
                    <p className="text-gray-500 text-lg mb-4">Affiliate not found</p>
                    <button
                        onClick={() => navigate(-1)}
                        className="text-orange-500 hover:text-orange-600"
                    >
                        Go Back
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-full p-4 lg:p-6">
            <div className="max-w-7xl mx-auto">
                <div className="bg-white shadow-lg rounded-xl p-6 ">
                    {/* Header Section */}
                    <div className="flex flex-col sm:flex-row sm:items-center lg:gap-4 mb-8">
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => navigate(-1)}
                                className="text-gray-600 hover:text-gray-800 p-2 rounded-full hover:bg-gray-100 transition-colors"
                            >
                                <FaArrowLeft className="w-5 h-5" />
                            </button>
                            <h2 className="lg:text-2xl text-xl font-bold text-gray-900">
                                {affiliate.firstName || affiliate.username || affiliate.email}'s Campaigns
                            </h2>
                        </div>

                        <div className="flex flex-col sm:flex-row sm:items-center sm:gap-2 text-gray-500 text-sm mt-1 sm:mt-0 ml-10 sm:ml-0">
                            <span className="px-2 py-1 bg-gray-100 rounded-full text-sm w-fit">
                                {affiliate.affiliateType}
                            </span>
                            <span className="hidden sm:inline">•</span>
                            <span>{affiliate.email}</span>
                        </div>
                    </div>

                    {/* Search and Filter Section */}
                    <div className="flex flex-col md:flex-row gap-4 mb-8">
                        <div className="relative flex-1">
                            <input
                                type="text"
                                placeholder="Search campaigns by name or description..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                            />
                            <FaSearch className="absolute left-3 top-3.5 text-gray-400" />
                        </div>
                        <div className="relative">
                            <select
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                                className="appearance-none pl-10 pr-8 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                            >
                                <option value="all">All Status</option>
                                <option value="pending">Pending</option>
                                <option value="accepted">Accepted</option>
                                <option value="rejected">Rejected</option>
                            </select>
                            <FaFilter className="absolute left-3 top-3.5 text-gray-400" />
                        </div>
                    </div>

                    {/* Campaigns Table */}
                    <div className="overflow-x-auto rounded-lg border border-gray-200">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Campaign Name
                                    </th>
                                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Description
                                    </th>
                                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Campaign Type
                                    </th>
                                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Commission
                                    </th>
                                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Status
                                    </th>
                                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {filteredCampaigns.length === 0 ? (
                                    <tr>
                                        <td colSpan="6" className="px-6 py-8 text-center">
                                            <div className="text-gray-500">
                                                No campaigns found
                                                {searchTerm && (
                                                    <p className="text-sm mt-1">
                                                        Try adjusting your search or filters
                                                    </p>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ) : (
                                    filteredCampaigns.map((campaignItem) => {
                                        const campaign = campaignItem.campaign;
                                        const isDeleting = deletingCampaign === campaignItem._id;
                                        const canDelete = campaignItem.status === 'pending' || campaignItem.status === 'rejected';
                                        
                                        return (
                                            <tr key={campaignItem._id} className="hover:bg-gray-50 transition-colors">
                                                <td className="px-6 py-4">
                                                    <div className="text-sm font-medium text-gray-900">
                                                        {campaign.name || 'N/A'}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <div className="text-sm text-gray-500 line-clamp-2">
                                                        {campaign.description || 'N/A'}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${
                                                        campaign.campaignType === 'Advertising' 
                                                            ? 'bg-blue-100 text-blue-800' 
                                                            : campaign.campaignType === 'Affiliate'
                                                                ? 'bg-green-100 text-green-800'
                                                                : 'bg-orange-100 text-orange-800'
                                                    }`}>
                                                        {campaign.campaignType}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="text-sm font-medium text-gray-900">
                                                        {campaign.commission?.model === 'fixed'
                                                            ? `${campaign.commission.amount} ${campaign.commission.currency?.symbol || ''}`
                                                            : campaign.commission?.model === 'percentage'
                                                                ? `${campaign.commission.percentage}%`
                                                                : 'N/A'}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    {getStatusBadge(campaignItem.status)}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <button
                                                        onClick={() => requestDeleteCampaign(campaignItem)}
                                                        disabled={!canDelete || isDeleting}
                                                        className={`p-2 rounded-lg transition-all duration-200 ${
                                                            canDelete
                                                                ? isDeleting
                                                                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                                                    : 'bg-red-100 text-red-600 hover:bg-red-200 hover:text-red-700'
                                                                : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                                        }`}
                                                        title={
                                                            campaignItem.status === 'accepted'
                                                                ? 'Cannot delete accepted campaigns'
                                                                : 'Delete campaign invitation'
                                                        }
                                                    >
                                                        <FaTrash className="w-4 h-4" />
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
            {/* Confirm Delete Modal */}
            <ConfirmModal
                isOpen={confirmOpen}
                title="Delete invitation?"
                message={campaignPendingDelete ? `Are you sure you want to delete the campaign invitation for "${campaignPendingDelete.campaign.name}"? This action cannot be undone.` : ''}
                confirmText={deletingCampaign ? 'Deleting…' : 'Delete'}
                cancelText="Cancel"
                isProcessing={Boolean(deletingCampaign)}
                tone="danger"
                onCancel={() => {
                    setConfirmOpen(false);
                    setCampaignPendingDelete(null);
                }}
                onConfirm={async () => {
                    if (!campaignPendingDelete) return;
                    await handleDeleteCampaign(campaignPendingDelete);
                    setConfirmOpen(false);
                    setCampaignPendingDelete(null);
                }}
            />
        </div>
    );
};

export default AffiliateCampaigns; 